import clsx from 'clsx';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import LabelT from '../Label/LabelT';

interface Options {
  [key: string]: any;
}

type ColorInputWithLabelProps<T extends FieldValues> = {
  field: Path<T>;
  options?: Options;
  control?: Control<T>;
  placeholder?: string;
  defaultColor?: string;
  label: string;
  id?: string;
  mainClasses?: string;
  labelClassName?: string;
  hexInputClassName?: string;
} & React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

const normalizeHexColor = (color: string) => {
  const normalizedColor = color.toUpperCase();

  if (normalizedColor.length === 4) {
    // Convert #RGB to #RRGGBB, in cases we have rgb colors like #fff
    return `#${normalizedColor[1]}${normalizedColor[1]}${normalizedColor[2]}${normalizedColor[2]}${normalizedColor[3]}${normalizedColor[3]}`;
  }

  return normalizedColor;
};

export function ColorInputWithLabelT<T extends FieldValues>({
  field,
  options,
  control,
  defaultColor = '#FFFFFF',
  placeholder,
  style,
  label,
  id,
  mainClasses,
  labelClassName = '',
  hexInputClassName = '',
  ...restParams
}: ColorInputWithLabelProps<T>) {
  const { className, ...rest } = restParams;
  const registerObject =
    field && control ? control.register(field, options) : {};

  return (
    <div className={`flex items-center gap-3 ${mainClasses} box-border`}>
      <LabelT text={`${label}:`} htmlFor={id} className={labelClassName} />

      <div className='flex items-center'>
        <Controller
          name={field}
          control={control}
          render={({ field: { onChange, value: oldValue } }) => {
            const value = normalizeHexColor(oldValue ?? defaultColor);

            return (
              <>
                <input
                  type='text'
                  value={value}
                  onChange={onChange}
                  className={`w-20 border border-gray-200 bg-red mr-2 text-sm rounded-md px-2 py-1 ${hexInputClassName}`}
                  placeholder='Hex value'
                />

                <div className='w-7 h-5 rounded-md overflow-hidden ring-1 ring-gray-200 box-border'>
                  <input
                    id={field}
                    value={value}
                    className={clsx(
                      'w-full',
                      'h-full',
                      'cursor-pointer',
                      'border-none',
                      'p-0',
                      'bg-white',
                      'shadow-md',
                      'transform',
                      'scale-150',
                      'box-border',
                      'appearance-none',
                      className,
                    )}
                    placeholder={placeholder}
                    type='color'
                    onInput={onChange}
                    {...rest}
                    {...registerObject}
                  />
                </div>
              </>
            );
          }}
        />
      </div>
    </div>
  );
}

export default ColorInputWithLabelT;
